import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  Upgrade as UpgradeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import api from '../utils/api';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  interval: string;
  features: string[];
}

interface SubscriptionStatus {
  user_type: string;
  subscription_status?: string;
  trial_expires_at?: string;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  is_trial_expired: boolean;
  meetings_count: number;
  trial_meetings_limit: number;
}

const PricingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load subscription plans
      const plansResponse = await api.get('/subscription/plans');
      setPlans(plansResponse.data);
      
      // Load user's subscription status if authenticated
      if (isAuthenticated) {
        const statusResponse = await api.get('/subscription/status');
        setSubscriptionStatus(statusResponse.data);
      }
    } catch (err: any) {
      console.error('Error loading pricing data:', err);
      setError('Failed to load pricing information');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (priceId: string) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    try {
      setUpgrading(priceId);
      setError(null);

      // Create checkout session
      const response = await api.post('/subscription/create-checkout', {
        price_id: priceId,
        success_url: `${window.location.origin}/dashboard?upgrade=success`,
        cancel_url: `${window.location.origin}/pricing?upgrade=cancelled`,
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (err: any) {
      console.error('Error creating checkout session:', err);
      setError(err.response?.data?.detail || 'Failed to start upgrade process');
      setUpgrading(null);
    }
  };

  const handleManageBilling = async () => {
    try {
      const response = await api.post('/subscription/billing-portal', {
        return_url: window.location.href,
      });
      window.location.href = response.data.portal_url;
    } catch (err: any) {
      console.error('Error accessing billing portal:', err);
      setError(err.response?.data?.detail || 'Failed to access billing portal');
    }
  };

  const formatPrice = (priceInCents: number) => {
    return `$${(priceInCents / 100).toFixed(0)}`;
  };

  const getCurrentPlanId = () => {
    return subscriptionStatus?.user_type === 'paid' ? 'paid' :
           subscriptionStatus?.user_type === 'pro' ? 'pro' : null;
  };

  const renderPlanCard = (plan: SubscriptionPlan) => {
    const currentPlanId = getCurrentPlanId();
    const isCurrentPlan = currentPlanId === plan.id;
    const isTrialUser = subscriptionStatus?.user_type === 'trial';
    
    // Allow upgrades for trial users to any plan, or for existing users to different plans
    const canUpgrade = isTrialUser || (currentPlanId !== plan.id);

    return (
      <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            border: isCurrentPlan ? `2px solid ${theme.palette.primary.main}` : '1px solid rgba(0, 0, 0, 0.12)',
            '&:hover': {
              boxShadow: theme.shadows[8],
            },
          }}
        >
          {plan.id === 'pro' && (
            <Chip
              label="Most Popular"
              color="primary"
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                fontWeight: 600,
              }}
            />
          )}
          
          <CardContent sx={{ flexGrow: 1, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              {plan.id === 'pro' ? (
                <StarIcon color="secondary" sx={{ mr: 1 }} />
              ) : (
                <UpgradeIcon color="action" sx={{ mr: 1 }} />
              )}
              <Typography variant="h5" component="h2" fontWeight={600}>
                {plan.name}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="h3" component="div" fontWeight={700} color="primary">
                {formatPrice(plan.price)}
                <Typography variant="body1" component="span" color="text.secondary">
                  /{plan.interval}
                </Typography>
              </Typography>
            </Box>

            <List disablePadding>
              {plan.features.map((feature, index) => (
                <ListItem key={index} disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>

          <CardActions sx={{ p: 3, pt: 0 }}>
            {isCurrentPlan ? (
              <Button
                fullWidth
                variant="outlined"
                onClick={handleManageBilling}
                disabled={!subscriptionStatus?.stripe_customer_id}
              >
                Manage Billing
              </Button>
            ) : canUpgrade && (
              <Button
                fullWidth
                variant={plan.id === 'pro' ? 'contained' : 'contained'}
                color={plan.id === 'pro' ? 'secondary' : 'primary'}
                onClick={() => handleUpgrade(`price_${plan.id}`)} // This will need actual Stripe price IDs
                disabled={upgrading === `price_${plan.id}`}
                startIcon={upgrading === `price_${plan.id}` ? <CircularProgress size={16} /> : undefined}
                sx={plan.id === 'pro' ? {
                  borderWidth: 2,
                  '&:hover': {
                    borderWidth: 2,
                  },
                } : undefined}
              >
                {upgrading === `price_${plan.id}` ? 'Processing...' : `Upgrade to ${plan.name}`}
              </Button>
            )}
          </CardActions>
        </Card>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading pricing information...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          fontWeight={700}
          gutterBottom
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Choose Your Plan
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          Unlock the full potential of AI-powered meeting transcription and analysis
        </Typography>
      </Box>

      {/* Trial Status Alert */}
      {isAuthenticated && subscriptionStatus?.user_type === 'trial' && (
        <Alert
          severity={subscriptionStatus.is_trial_expired ? 'error' : 'warning'}
          sx={{ mb: 4 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {subscriptionStatus.is_trial_expired ? 'Trial Expired' : 'Trial Account'}
          </Typography>
          <Typography variant="body2">
            {subscriptionStatus.is_trial_expired ? (
              'Your trial has expired. Please upgrade to continue using the service.'
            ) : (
              `You've used ${subscriptionStatus.meetings_count}/${subscriptionStatus.trial_meetings_limit} meetings.`
            )}
          </Typography>
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Pricing Cards */}
      <Grid container spacing={4} justifyContent="center">
        {/* Free Trial Card */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              '&:hover': {
                boxShadow: theme.shadows[8],
              },
            }}
          >
            <CardContent sx={{ flexGrow: 1, p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <StarIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="h5" component="h2" fontWeight={600}>
                  Free Trial
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h3" component="div" fontWeight={700} color="primary">
                  Free
                </Typography>
              </Box>

              <List disablePadding>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="5 meetings included" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="5 minutes per meeting" />
                </ListItem>
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon color="primary" fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Full feature access" />
                </ListItem>
              </List>
            </CardContent>

            <CardActions sx={{ p: 3, pt: 0 }}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={() => navigate('/dashboard')}
              >
                Start Free Trial
              </Button>
            </CardActions>
          </Card>
        </Grid>
        
        {plans.map((plan) => (
          <Grid item xs={12} md={4} key={plan.id}>
            {renderPlanCard(plan)}
          </Grid>
        ))}
      </Grid>

      {/* FAQ or Additional Info */}
      <Box sx={{ mt: 8, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Need help choosing?
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Start with our free trial to experience the full power of AI transcription.
        </Typography>
        {!isAuthenticated && (
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/register')}
          >
            Start Free Trial
          </Button>
        )}
      </Box>
    </Container>
  );
};

export default PricingPage; 